import React from 'react';
import ValidatorSummary, { IValidatorDecisionConfig } from 'components/ValidatorSummary';
import { AsyncTaskStatus } from 'constants/conceptLinkerValidatorStatus';
import { AIIllustrationItemsModel } from 'models/AIIllustrationItemsModel';
import { AIIllustrationItemStatus } from 'types/aiIllustration';

interface IProps {
  itemsModel: AIIllustrationItemsModel;
  onFilterChange: (filter: { type: string; value: string } | null) => void;
  activeFilter: { type: string; value: string } | null;
  onClearFilter: () => void;
}

const AIIllustrationSummary: React.FC<IProps> = ({ itemsModel, onFilterChange, activeFilter, onClearFilter }) => {
  // Map item statuses to decisions (only for DONE items)
  const decisions: IValidatorDecisionConfig[] = [{ key: 'done', label: 'Done', color: '#4CAF50' }];

  // ValidatorSummary expects a single PENDING status value
  const NORMALIZED_PROCESSING = 999; // Use a unique value for processing items

  const statusConfig = {
    PENDING: NORMALIZED_PROCESSING, // Show items with this normalized status as processing
    NEEDS_USER_REVIEW: AIIllustrationItemStatus.NEEDS_REVIEW, // 4 - use ValidatorSummary's built-in review status
    DONE: AIIllustrationItemStatus.DONE, // 2
  };

  const asyncTaskStatusConfig = {
    FAILED: AsyncTaskStatus.FAILED,
    DEAD_LETTER: AsyncTaskStatus.DEAD_LETTER,
  };

  // Adapter for filter change - unify processing and review into a single filter
  const handleFilterChange = (type: string, value: string) => {
    // When clicking on "processing" or "review", use unified filter to show both
    if (type === 'status' && (value === 'processing' || value === 'review')) {
      onFilterChange({ type: 'status', value: 'pending_review' });
    } else {
      onFilterChange({ type, value });
    }
  };

  // Check if unified filter is active (for highlighting both badges)
  const isUnifiedFilterActive = activeFilter?.type === 'status' && activeFilter?.value === 'pending_review';

  // Create modified activeFilter to highlight both processing and review when unified filter is active
  const getDisplayActiveFilter = () => {
    // Return a special marker that ValidatorSummary doesn't need to know about
    // We'll handle the visual highlighting separately
    return activeFilter;
  };

  // Map items to validator format
  const items = ((itemsModel.gridData as any[]) || []).map((item) => {
    // Determine the status to pass to ValidatorSummary
    let mappedStatus = item.status;

    // Normalize PENDING (0) and PROCESSING (1) to show as "processing"
    if (item.status === AIIllustrationItemStatus.PENDING || item.status === AIIllustrationItemStatus.PROCESSING) {
      mappedStatus = NORMALIZED_PROCESSING;
    }
    // NEEDS_REVIEW (4) stays as-is, will be caught by statusConfig.NEEDS_USER_REVIEW
    // DONE (2) stays as-is, will be caught by statusConfig.DONE

    // Map item status to decision field (only for DONE items)
    let decision: string | undefined;
    if (item.status === AIIllustrationItemStatus.DONE) {
      decision = 'done';
    }
    // NEEDS_REVIEW items should NOT have a decision - they're handled by status instead

    return {
      ...item,
      status: mappedStatus,
      decision,
      ai_result: {
        decision,
      },
    };
  });

  return (
    <ValidatorSummary
      items={items}
      decisions={decisions}
      statusConfig={statusConfig}
      asyncTaskStatus={asyncTaskStatusConfig}
      onFilterChange={handleFilterChange}
      activeFilter={activeFilter}
      onClearFilter={onClearFilter}
      enableChangeGrouping={false}
      customFeatures={{}}
      colors={{
        processing: '#9C27B0',
        review: '#FFA726',
        error: '#E53935',
      }}
      unifiedProcessingReviewFilter={isUnifiedFilterActive}
    />
  );
};

export default AIIllustrationSummary;
